import { Checkbox, FormControlLabel, FormGroup, IconButton, Menu, Dialog, Button, MenuItem, Select, Tooltip } from "@mui/material";
import * as turf from "@turf/turf";
import { agreement, getAmapGeo, getAmapPlaceText, getAmapRegeo, getAuth, logout } from "api/layer";
import china from "assets/json/c1.json";
import country from "assets/json/country.json";
import Input from "components/input/input";
import { LeftPanel, RightPanel, zoomLevelMax } from "helpers/constants";
import { AppAction, AppContext, setLeftPanel } from "helpers/context";
import { MapContext } from "helpers/map";
import { guardLanguage, langCn, langEn, setLanguage } from "helpers/translations";
import i18next from "i18next";
import { useContext, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import { ReactComponent as ReleaseSvg } from "assets/release.svg";
import { ReactComponent as ContactSvg } from "assets/contact.svg";
import { ReactComponent as DataSourceSvg } from "assets/data_source.svg";
import { ReactComponent as IconHelpSvg } from "assets/icon_help.svg";
import { ReactComponent as LogoutSvg } from "assets/logout.svg";
import { ReactComponent as TermsSvg } from "assets/terms.svg";
import { ReactComponent as BasicSvg } from "assets/basic.svg";
import { ReactComponent as PremiumSvg } from "assets/premium.svg";
import { ReactComponent as PremiumAddSvg } from "assets/premium+.svg";
import styles from "./header.module.scss";
import Location from "./location/location";
import TipModal from "./info-modal/info-modal";
import HeaderNotification from "containers/messages/header-notification/notification";

let chinaGeojson = turf.polygon(china.features[0].geometry.rings);
function Header() {
	const [context, dispatch] = useContext(AppContext);
	const { AMap, map, } = useContext(MapContext);
	const [search, setSearch] = useState<string>("");
	const [nationSearchSelect, setNationSearchSelect] = useState<string>(context.language === "cn" ? "中国" : "China");
	const [loginAccount, setLoginAccount] = useState<string>("AI@swissre");
	const [miniName, setMiniName] = useState<string>("AI");
	const [userName, setUserName] = useState<string>("AI");
	const [selectSearchType, setSelectSearchType] = useState<string>("1");
	const [showSearch, setShowSearch] = useState<boolean>(false);
	const [showNation, setShowNation] = useState<boolean>(false);
	const [infoModalOpened, setInfoModalOpened] = useState<boolean>(false);
	const [termsOfUseModalOpened, setTermsOfUseModalOpened] = useState<boolean>(false);
	const [termsOfUseChecked, setTermsOfUseChecked] = useState<boolean>(false);
	const [infoModalType, setInfoModalType] = useState<string>("");
	const [searchData, setSearchData] = useState<any>({ cn: [], en: [] });
	const [t] = useTranslation();
	const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
	const [avatarEl, setAvatarEl] = useState<null | HTMLElement>(null);
	const open = Boolean(anchorEl);
	const avataOpen = Boolean(avatarEl);
	const [tipModel, setTipModel] = useState<boolean>(false);

	const hasRoleLike = (...keywords: string[]) => {
		if (!context.userRoles?.length) return false;
		const normalizedKeywords = keywords.map((keyword) => keyword.toLowerCase());
		return context.userRoles.some((role) => {
			const normalizedRole = String(role).toLowerCase();
			return normalizedKeywords.some((keyword) => normalizedRole.includes(keyword));
		});
	};

	type RoleKey = "premium+" | "premium" | "basic" | "trial" | "admin";

	const matchedRoleKey = useMemo<RoleKey | undefined>(() => {
		const roleMatchers: Array<{ key: RoleKey; keywords: string[] }> = [
			{ key: "premium+", keywords: ["premium+", "高级版+"] },
			{ key: "premium", keywords: ["premium", "高级版"] },
			{ key: "basic", keywords: ["basic", "基础版"] },
			{ key: "trial", keywords: ["trial", "试用版"] },
			{ key: "admin", keywords: ["admin", "管理版"] },
		];

		return roleMatchers.find((item) => hasRoleLike(...item.keywords))?.key;
	}, [context.userRoles]);

	const systemBg = () => {
		switch (matchedRoleKey) {
			case "premium+":
				return "var(--premium-gradient-2, linear-gradient(180deg, #fbbb18 0%, #b77718 100%))";
			case "premium":
				return "linear-gradient(180deg, #9D9D9D 0%, #747474 100%)";
			case "basic":
				return "#0655CB";
			case "trial":
				return "#627d77";
			default:
				return "green";
		}
	}

	const systemText = () => {
		if (matchedRoleKey) {
			return t(`userSystem.roleSelect.${matchedRoleKey}`);
		}
		return context.userRoles?.[0];
	}

	const systemSvg = (props: any) => {
		switch (matchedRoleKey) {
			case "premium+":
				return <PremiumAddSvg className={props.className} />;
			case "premium":
				return <PremiumSvg className={props.className} />;
			case "basic":
				return <BasicSvg className={props.className} />;
			default:
				return null;
		}
	}

	const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
		setAnchorEl(event.currentTarget);
	};

	const handleClose = () => {
		setAnchorEl(null);
	};

	const handleAvatarClick = (event: React.MouseEvent<HTMLButtonElement>) => {
		setAvatarEl(event.currentTarget);
	};

	const handleAvatarClose = () => {
		setAvatarEl(null);
	};

	const filterCountry = country.filter((item) => item.label.toLowerCase().includes(nationSearchSelect.toLowerCase()));

	const changeLanguage = (lang: string): void => {
		setLanguage(lang);
		i18next.changeLanguage(guardLanguage(lang));
		dispatch({ type: AppAction.setLanguage, payload: lang });
	};

	const handleUpdateData = (resultData: any) => {
		try {
			return {
				...resultData,
				"cn": {
					...resultData.cn,
					"spatial": Array.isArray(resultData.cn.spatial) ? null : resultData.cn.spatial,
					"location": Array.isArray(resultData.cn.location) ? null : resultData.cn.location,
					"oversea": Array.isArray(resultData.cn.oversea) ? null : resultData.cn.oversea,
					"city": Array.isArray(resultData.cn.city) ? null : resultData.cn.city,
					"province": Array.isArray(resultData.cn.province) ? null : resultData.cn.province,
					"adcode": Array.isArray(resultData.cn.adcode) ? null : resultData.cn.adcode,
					"district": Array.isArray(resultData.cn.district) ? null : resultData.cn.district,
					"towncode": Array.isArray(resultData.cn.towncode) ? null : resultData.cn.towncode,
					"streetNumber": {
						...(resultData.cn.streetNumber ?? {}),
						"number": Array.isArray(resultData.cn.streetNumber.number) ? null : resultData.cn.streetNumber.number,
						"direction": Array.isArray(resultData.cn.streetNumber.direction) ? null : resultData.cn.streetNumber.direction,
						"distance": Array.isArray(resultData.cn.streetNumber.distance) ? null : resultData.cn.streetNumber.distance,
						"street": Array.isArray(resultData.cn.streetNumber.street) ? null : resultData.cn.streetNumber.street
					},
					"country": Array.isArray(resultData.cn.country) ? null : resultData.cn.country,
					"township": Array.isArray(resultData.cn.township) ? null : resultData.cn.township,
					"building": {
						...(resultData.cn.building ?? {}),
						"name": Array.isArray(resultData.cn.building.name) ? null : resultData.cn.building.name,
						"type": Array.isArray(resultData.cn.building.type) ? null : resultData.cn.building.type
					},
					"neighborhood": {
						...(resultData.cn.neighborhood ?? {}),
						"name": Array.isArray(resultData.cn.neighborhood.name) ? null : resultData.cn.neighborhood.name,
						"type": Array.isArray(resultData.cn.neighborhood.type) ? null : resultData.cn.neighborhood.type
					},
					"citycode": Array.isArray(resultData.cn.citycode) ? null : resultData.cn.citycode,
					"formatted_address": Array.isArray(resultData.cn.formatted_address) ? null : resultData.cn.formatted_address
				},
				"en": {
					...resultData.en,
					"spatial": Array.isArray(resultData.en.spatial) ? null : resultData.en.spatial,
					"location": Array.isArray(resultData.en.location) ? null : resultData.en.location,
					"oversea": Array.isArray(resultData.en.oversea) ? null : resultData.en.oversea,
					"city": Array.isArray(resultData.en.city) ? null : resultData.en.city,
					"province": Array.isArray(resultData.en.province) ? null : resultData.en.province,
					"adcode": Array.isArray(resultData.en.adcode) ? null : resultData.en.adcode,
					"district": Array.isArray(resultData.en.district) ? null : resultData.en.district,
					"towncode": Array.isArray(resultData.en.towncode) ? null : resultData.en.towncode,
					"streetNumber": {
						...(resultData.en.streetNumber ?? {}),
						"number": Array.isArray(resultData.en.streetNumber.number) ? null : resultData.en.streetNumber.number,
						"direction": Array.isArray(resultData.en.streetNumber.direction) ? null : resultData.en.streetNumber.direction,
						"distance": Array.isArray(resultData.en.streetNumber.distance) ? null : resultData.en.streetNumber.distance,
						"street": Array.isArray(resultData.en.streetNumber.street) ? null : resultData.en.streetNumber.street
					},
					"country": Array.isArray(resultData.en.country) ? null : resultData.en.country,
					"township": Array.isArray(resultData.en.towncode) ? null : resultData.en.towncode,
					"building": {
						...(resultData.en.building ?? {}),
						"name": Array.isArray(resultData.en.building.name) ? null : resultData.en.building.name,
						"type": Array.isArray(resultData.en.building.type) ? null : resultData.en.building.type
					},
					"neighborhood": {
						...(resultData.en.neighborhood ?? {}),
						"name": Array.isArray(resultData.en.neighborhood.name) ? null : resultData.en.neighborhood.name,
						"type": Array.isArray(resultData.en.neighborhood.type) ? null : resultData.en.neighborhood.type
					},
					"citycode": Array.isArray(resultData.en.citycode) ? null : resultData.en.citycode,
					"formatted_address": Array.isArray(resultData.en.formatted_address) ? null : resultData.en.formatted_address
				},
				"spatial": Array.isArray(resultData.spatial) ? null : resultData.spatial,
				"location": Array.isArray(resultData.location) ? null : resultData.location,
				"oversea": Array.isArray(resultData.oversea) ? null : resultData.oversea
			}
		} catch (error) {
			console.log(error, 'error');
			return resultData
		}
	}

	const getLangClass = (lang: string): string => (context.language === lang ? styles.langSelected : styles.lang);

	const selectSwissRe = (resultData: any, isZoom: boolean = true): void => {
		dispatch({
			type: AppAction.setSspData,
			payload: undefined
		});
		let poiResultItem: any = handleUpdateData(resultData);
		isZoom && map.setZoom(zoomLevelMax);
		dispatch({ type: AppAction.setSearchLocationInfo, payload: poiResultItem });
		dispatch({ type: AppAction.setSearchMapCenter, payload: [poiResultItem[context.language].location.split(",")[0], poiResultItem[context.language].location.split(",")[1]] });
		dispatch({ type: AppAction.setAnalysisData, payload: null });
		dispatch({ type: AppAction.setClimateRiskCategorySspData, payload: undefined });
		dispatch({ type: AppAction.setBatchId, payload: undefined });
		dispatch({ type: AppAction.setFutureHazardBatchId, payload: undefined });
		dispatch({ type: AppAction.setCrsComponentBatchId, payload: undefined });
		dispatch({ type: AppAction.setSingleAnalysisSelectedLayers, payload: undefined });
		dispatch({ type: AppAction.setSingleLocationAnalysed, payload: false });
		dispatch({ type: AppAction.setCurrentMultipleLocationInfo, payload: undefined });
		dispatch({ type: AppAction.setRightPanel, payload: RightPanel.Single });
		// dispatch({ type: AppAction.setRightPanel, payload: (context.leftPanel === LeftPanel.Empty || context.leftPanel === LeftPanel.Layers) ? RightPanel.Single : RightPanel.Multiple });
	};

	const getSearchData = async (event: any) => {
		let nationObj = country.find((item) => item.label.toLowerCase().includes(nationSearchSelect.toLowerCase()));
		const nationCountryAdcode = nationObj?.country_adcode ?? nationObj?.value ?? nationSearchSelect;
		let params: any = {
			keywords: event.target.value,
			// country: nationObj.value,
			// city: nationObj.value,
			offset: 10,
			extensions: "all"
		}
		// //////  地理编码获取地址的接口
		// let res: any = await getAmapGeo({
		// 	address: event.target.value,
		// 	country: nationObj.value,
		// });
		if (nationObj?.value !== "CN") {
			params.country = nationCountryAdcode;
			params.city = nationCountryAdcode;
		}
		////// 关键字搜索获取地址的接口
		let res: any = await getAmapPlaceText(params)
		if (res.data && res.data[0] && res.data[0].status === "1") {
			// setSearchData({ cn: res.data[0].geocodes, en: res.data[1].geocodes, country: nationObj.value });
			setSearchData({ cn: res.data[0].pois, en: res.data[1].pois, country: nationCountryAdcode });
		} else {
			setSearchData({ cn: [], en: [], country: nationCountryAdcode });
		}
	};

	const searArea = debounce((event: any) => {
		if (!event.target.value) {
			setSearchData({ cn: [], en: [] });
			setShowSearch(false);
		} else {
			setShowSearch(true);
			// 调用 getSearchData 函数，并传递事件对象
			getSearchData(event);
		}
	}, 500);

	const handleInfoModalType = (type: string): void => {
		handleClose();
		setInfoModalOpened(true);
		setInfoModalType(type);
	};

	const handleChange = (event: any) => {
		setSelectSearchType(event.target.value);
		setSearch("");
		setSearchData({ cn: [], en: [] });
	};

	const handleTipModal = () => {
		dispatch({
			type: AppAction.setTipModal,
			payload: {
				name: (
					<div style={{ display: "flex", alignItems: "center" }}>
						<span
							className="material-icons"
							style={{ marginRight: "8px" }}
						>info</span>
						{t("tip.title")}
					</div>
				),
				description: t("tip.message"),
			},
		});
	};

	const checkInput = (location: string) => {
		let locationArr: any = [];
		if (location.indexOf(",") > -1 || location.indexOf("，") > -1) {
			let str = location.trim().split(" ").join("");
			if (location.indexOf(",") > -1) {
				locationArr = str.split(",");
			} else if (location.indexOf("，") > -1) {
				locationArr = str.split("，");
			}
			if (locationArr[0].endsWith('°') || locationArr[0].endsWith('E') || locationArr[0].endsWith('e') || locationArr[0].endsWith('W') || locationArr[0].endsWith('w')) {
				locationArr[0] = locationArr[0].slice(0, locationArr[0].length - 1)
			}

			if (locationArr[1].endsWith('°') || locationArr[1].endsWith('N') || locationArr[1].endsWith('n') || locationArr[1].endsWith('S') || locationArr[1].endsWith('s')) {
				locationArr[1] = locationArr[1].slice(0, locationArr[1].length - 1)
			}
		} else if (location.indexOf(";") > -1 || location.indexOf("；") > -1) {
			let str = location.trim().split(" ").join("");
			if (location.indexOf(";") > -1) {
				locationArr = str.split(";");
			} else if (location.indexOf("；") > -1) {
				locationArr = str.split("；");
			}
		} else if (location.indexOf("/") > -1 || location.indexOf("／") > -1) {
			let str = location.trim().split(" ").join("");
			if (location.indexOf("/") > -1) {
				locationArr = str.split("/");
			} else if (location.indexOf("／") > -1) {
				locationArr = str.split("／");
			}
		} else if (location.indexOf(" ") > -1) {
			locationArr = location.split(" ").filter((item: any) => item !== "");
		}

		if (locationArr.length !== 2) {
			return false;
		}

		const lon = locationArr[0];
		const lat = locationArr[1];
		if (isNaN(Number(lon)) || isNaN(Number(lat))) {
			return false;
		}

		const longitudeNumber = Number(lon);
		const latitudeNumber = Number(lat);

		if (longitudeNumber >= -180 && longitudeNumber <= 180 && latitudeNumber >= -90 && latitudeNumber <= 90) {
			// return true;
			return `${longitudeNumber},${latitudeNumber}`;
		}

		return false;
	};

	const handleDownEnter = async (event: any) => {
		const location = checkInput(event.target.value);

		event.stopPropagation();
		if (!location) {
			handleTipModal();
			event.preventDefault();
			event.stopPropagation();
			return;
		}
		// const location = event.target.value.trim().split(" ").join("");
		let params = {
			spatial: "",
			location,
			oversea: false,
		};
		if (selectSearchType === "2") {
			params.spatial = "gcj";
		} else if (selectSearchType === "3") {
			params.spatial = "wgs";
			params.oversea = true;
		} else if (selectSearchType === "4") {
			params.spatial = "wgs";
		}
		let res: any = await getAmapRegeo(params);
		if (res.data.length) {
			let location = res.data[0]?.regeocode?.addressComponent?.streetNumber?.location || params.location;
			selectSwissRe({
				cn: {
					...params,
					location: res.data[0]?.location,
					oversea: res.data[0]?.oversea,
					spatial: res.data[0]?.spatial,
					...res.data[0]?.regeocode?.addressComponent,
					formatted_address: res.data[0]?.regeocode?.formatted_address,
				},
				en: {
					...params,
					location: res.data[1]?.location,
					oversea: res.data[1]?.oversea,
					spatial: res.data[1]?.spatial,
					...res.data[1]?.regeocode?.addressComponent,
					formatted_address: res.data[1]?.regeocode?.formatted_address,
				},
				...params,
				location: location,
			});
		}
	};

	//防抖 N秒内重复触发的话只触发最后一次
	function debounce<T extends (...args: any[]) => void>(fn: T, delay: number, immediate = false, executeImmediately = false): (...args: Parameters<T>) => void {
		let timer: NodeJS.Timeout | null = null;
		let isInvoke = false;

		const _debounce = function (this: unknown, ...args: Parameters<T>): void {
			const executeNow = immediate && !isInvoke && executeImmediately;

			if (timer) clearTimeout(timer);

			if (executeNow) {
				fn.apply(this, args);
				isInvoke = true;
			} else {
				timer = setTimeout(() => {
					fn.apply(this, args);
					isInvoke = false;
				}, delay);
			}
		};

		return _debounce;
	}

	const getAuthUser = () => {
		getAuth().then((res: any) => {
			if (res.data?.details?.menus?.length > 0) {
				dispatch({ type: AppAction.setMenu, payload: res.data.details.menus });
			}
			const loginAccount = res.data.loginAccount;
			const miniName = res.data.details.miniName;
			const userName = res.data.details.firstName && res.data.details.lastName ? `${res.data.details.firstName} ${res.data.details.lastName}` : res.data.userName;
			const loginFirstTime = res.data.details.loginFirstTime;
			dispatch({ type: AppAction.setUserRoles, payload: res.data.details.roles.map((role: any) => role.roleName) });
			dispatch({ type: AppAction.setUserInfo, payload: res.data });
			setLoginAccount(loginAccount);
			setMiniName(miniName);
			setUserName(userName);
			if (loginFirstTime === 0) {
				setTermsOfUseModalOpened(true);
			}
		});
	};

	const handleTermsOfUseConfirm = () => {
		agreement().then((res: any) => {
			setTermsOfUseModalOpened(false);
		});
	};

	const logoutAccount = () => {
		// window.location.href=`https://non-prod.identity.swissre.cn/enduser/sp/logout/idaas-cn-pe335gkeq01plugin_oauth22?force=true&redirect_url=${process.env.REACT_APP_BASE_API}`
		logout().then((res) => {
			sessionStorage.clear();
			window.location.href = res.data.redirectUrl;
		});
	};

	const mapClickFn = async (e: any) => {
		let point = [e.lnglat.getLng(), e.lnglat.getLat()];
		let params = {
			spatial: "gcj",
			location: point.join(","),
			oversea: false,
		};
		if (!turf.booleanPointInPolygon(point, chinaGeojson)) {
			params.spatial = "wgs";
			params.oversea = true;
		}
		let res = await getAmapRegeo(params);
		if (res.data.length) {
			let location = res.data[0]?.regeocode?.addressComponent?.streetNumber?.location || params.location;
			selectSwissRe(
				{
					cn: {
						...params,
						location: res.data[0]?.location,
						oversea: res.data[0]?.oversea,
						spatial: res.data[0]?.spatial,
						...res.data[0]?.regeocode?.addressComponent,
						formatted_address: res.data[0]?.regeocode?.formatted_address,
					},
					en: {
						...params,
						location: res.data[1]?.location,
						oversea: res.data[1]?.oversea,
						spatial: res.data[1]?.spatial,
						...res.data[1]?.regeocode?.addressComponent,
						formatted_address: res.data[1]?.regeocode?.formatted_address,
					},
					...params,
					location: location,
				},
				false
			);
		}
	};

	const handleOpenReleasePage = () => {
		handleClose();
		window.open(window.origin + "/resources?type=release", "_blank");
	};

	const handleOpenDataSourcePage = () => {
		handleClose();
		window.open(window.origin + "/resources?type=dataSource", "_blank");
	};

	const handleOpenFaqPage = () => {
		handleClose();
		window.open(window.origin + "/resources?type=faq", "_blank");
	};

	// useEffect(() => {
	// 	if (!context.mapClickState) {
	// 		map?.on("click", mapClickFn);
	// 	} else {
	// 		map.off("click", mapClickFn);
	// 	}

	// 	return () => {
	// 		if (map) {
	// 			map.off("click", mapClickFn);
	// 		}
	// 	};
	// }, [map, context.mapClickState, context.leftPanel]);
	useEffect(() => {
		if (context.mapClickEvent) {
			mapClickFn(context.mapClickEvent)
		}
	}, [context.mapClickEvent])

	useEffect(() => {
		getAuthUser();
	}, []);

	return (
		<div className={styles.header}>
			<div className={styles.logo}>
				<Link
					to="/"
					className="pr-6"
					onClick={() => setLeftPanel(dispatch, LeftPanel.Empty)}
				>
					<img
						// title="SwissRe_Logo"
						alt="SwissRe_Logo"
						src="/SwissRe_Logo.svg"
					/>
				</Link>
				<div className="text-[19px]">
					CatNet<sup>®</sup>
				</div>
				{context.userRoles && (
					<div
						className="flex items-center justify-center ml-6 rounded-[10px] text-white text-[12px] py-[3px] px-[5px] min-w-[70px] h-[20px]"
						style={{
							background: systemBg(),
						}}
					>
						{systemSvg({ className: "mr-1 w-3 h-3" })}
						<span>{systemText()}</span>
					</div>
				)}
			</div>
			<div className={styles.search}>
				{selectSearchType === "1" ? (
					<div className={styles.nationSearch}>
						<div className={`material-icons ${styles.nationSearchIcon}`}>search</div>
						<input
							value={nationSearchSelect}
							className={styles.nationSearchSelect}
							placeholder={t("header.country")}
							onFocus={() => setTimeout(() => setShowNation(true), 100)}
							onBlur={() => setTimeout(() => setShowNation(false), 200)}
							onChange={(event: any) => setNationSearchSelect(event.target.value)}
						/>
						<span></span>
						<input
							value={search}
							className={styles.nationSearchInput}
							placeholder={t("header.placeholderAddress")}
							onFocus={() => setTimeout(() => searchData[context.language].length > 0 && setShowSearch(true), 500)}
							onBlur={() => setTimeout(() => setShowSearch(false), 500)}
							onChange={(event) => {
								setSearch(event.target.value);
								searArea(event);
							}}
						/>
						{search && (
							<div
								className={`material-icons ${styles.close}`}
								onClick={() => setSearch("")}
								role="button"
								tabIndex={0}
							>
								close
							</div>
						)}
					</div>
				) : (
					<Input
						value={search}
						icon="search"
						isClose={true}
						id="tipinput"
						placeholder={t("header.placeholderCoordinates")}
						onChange={(event) => setSearch(event.target.value)}
						onDownEnter={handleDownEnter}
						onClose={() => setSearch("")}
					/>
				)}
				{<TipModal />}
				{showNation && nationSearchSelect && (
					<div className={styles.nationLocations}>
						{filterCountry.map((item, index) => (
							<div
								className={styles.nationLocation}
								key={index}
								onClick={() => setNationSearchSelect(item.label)}
								role="button"
								tabIndex={0}
							>
								<Location text={item.label} />
							</div>
						))}
					</div>
				)}

				{showSearch && (
					<div className={styles.locations}>
						{searchData[context.language].length === 0 ? (
							<div
								className="loading loading-spinner loading-lg text-center w-full h-[40px]"
								style={{ maskSize: "auto" }}
							></div>
						) : (
							searchData[context.language].map((item: any, index: number) => (
								<div
									className={styles.location}
									role="button"
									tabIndex={0}
									onClick={() =>
										selectSwissRe({
											cn: {
												...searchData.cn[index],
												formatted_address: searchData.cn[index].name,
												province: searchData.cn[index].pname,
												city: searchData.cn[index].cityname,
												district: searchData.cn[index].adname,
												streetNumber: {
													street: searchData.cn[index].address
												}
											},
											en: {
												...searchData.en[index],
												formatted_address: searchData.en[index].name,
												province: searchData.en[index].pname,
												city: searchData.en[index].cityname,
												district: searchData.en[index].adname,
												streetNumber: {
													street: searchData.en[index].address
												}
											},
											country: searchData.country,
										})
									}
									key={index}
								>
									<Location
										text={item && item.name}
										showIcon={true}
									/>
								</div>
							))
						)}
					</div>
				)}
			</div>
			<div className={`flex items-center ${styles.right}`}>
				<Select
					// title="change geo scheme"
					className={styles.geo}
					value={selectSearchType}
					onChange={handleChange}
					color="success"
				>
					{/* <option value="1" >
            {t("header.geo.addressInside")}
          </option>
          <option value="2">{t("header.geo.addressOutside")}</option> */}
					<MenuItem value="1">{t("header.geo.addressInformation")}</MenuItem>
					<MenuItem value="2">{t("header.geo.gcj02")}</MenuItem>
					<MenuItem value="4">{t("header.geo.wsg84Inside")}</MenuItem>
					<MenuItem value="3">{t("header.geo.wsg84Outside")}</MenuItem>
				</Select>
				<HeaderNotification />
				<div
					className="dropdown dropdown-end"
				>
					<Tooltip
						title={t("header.help.title")}
						placement="bottom"
						slotProps={{
							popper: {
								modifiers: [
									{
										name: 'offset',
										options: {
											offset: [0, -12],
										},
									},
								],
							},
						}}
					>
						<IconButton
							id="basic-button"
							aria-controls={open ? "basic-menu" : undefined}
							aria-haspopup="true"
							aria-expanded={open ? "true" : undefined}
							onClick={handleClick}
							className={styles.customIconButton}
						>
							<IconHelpSvg className={`${open ? "fill-[#007934]" : "fill-[#0009]"} m-[4px] focus:outline-none`} />
						</IconButton>
					</Tooltip>

					<Menu
						id="basic-menu"
						anchorEl={anchorEl}
						open={open}
						onClose={handleClose}
						MenuListProps={{
							"aria-labelledby": "basic-button",
						}}
						PaperProps={{
							elevation: 10,
							sx: {
								"& .MuiList-root": {
									py: 0,
								},
							},
						}}
					>
						<ul className="card compact dropdown-content z-[1] p-0 shadow bg-base-100  w-[200px] rounded-[4px] overflow-hidden">
							<li>
								<div className="flex justify-between items-center rounded-none text-[14px] px-4 py-0 h-12">
									<span className="text-[#0009] font-medium">{t("header.help.title")}</span>
									<span className="text-[#00000061] font-normal">{t("header.help.ver")} 1.8.1</span>
								</div>
							</li>
							<div className="border-t-[1px] border-solid border-[#0000001f]"></div>
							<li
								className={styles.dropdownContent}
								onClick={() => handleOpenDataSourcePage()}
								role="button"
								tabIndex={0}
							>
								<div className="flex items-center rounded-none text-[14px] px-4 py-0 h-12 font-normal">
									<DataSourceSvg className="mr-4 w-6 h-6 fill-[#0000008a]" />
									<span className="text-[#000000de]">{t("header.help.dataSources")}</span>
								</div>
							</li>
							<div className="border-t-[1px] border-solid border-[#0000001f]"></div>
							<li
								className={styles.dropdownContent}
								onClick={() => handleInfoModalType("termsOfUse")}
								role="button"
								tabIndex={0}
							>
								<div className="flex items-center rounded-none text-[14px] px-4 py-0 h-12 font-normal">
									<TermsSvg className="mr-4 w-6 h-6 fill-[#0000008a]" />
									<span className="text-[#000000de]">{t("header.help.termsOfUse")}</span>
								</div>
							</li>
							<div className="border-t-[1px] border-solid border-[#0000001f]"></div>
							<li
								className={styles.dropdownContent}
								onClick={() => handleInfoModalType("contact")}
								role="button"
								tabIndex={0}
							>
								<div className="flex items-center rounded-none text-[14px] px-4 py-0 h-12 font-normal">
									<ContactSvg className="mr-4 w-6 h-6 fill-[#0000008a]" />
									<span className="text-[#000000de]">{t("header.help.contact")}</span>
								</div>
							</li>
							<div className="border-t-[1px] border-solid border-[#0000001f]"></div>
							<li
								className={styles.dropdownContent}
								onClick={() => handleOpenReleasePage()}
								role="button"
								tabIndex={0}
							>
								<div className="flex items-center rounded-none text-[14px] px-4 py-0 h-12 font-normal">
									<ReleaseSvg className="mr-4 w-6 h-6 fill-[#0000008a]" />
									<span className="text-[#000000de]">
										{t("header.help.releaseInfo")}
									</span>
								</div>
							</li>
							<div className="border-t-[1px] border-solid border-[#0000001f]"></div>
							<li
								className={styles.dropdownContent}
								onClick={() => handleOpenFaqPage()}
								role="button"
								tabIndex={0}
							>
								<div className="flex items-center rounded-none text-[14px] px-4 py-0 h-12 font-normal">
									<span className="material-icons mr-4 w-6 h-6 text-[#0000008a]">quiz</span>
									<span className="text-[#000000de]">{t("header.help.faq")}</span>
								</div>
							</li>
						</ul>
					</Menu>

					<Dialog
						open={infoModalOpened}
						onClose={() => setInfoModalOpened(false)}
						sx={{
							"& .MuiDialog-paper": {
								maxWidth: "80vw",
								maxHeight: "85vh",
								borderRadius: "4px",
								margin: "0"
							}
						}}
					>
						<div className={styles.DialogContainer}>
							<div className={styles.DialogHeader}>{infoModalType && t(`header.help.infoModal.${infoModalType}.header`)}</div>
							<div className={styles.text}>
								{infoModalType === "termsOfUse" ? (
									<embed
										src={`${process.env.PUBLIC_URL}/tou/${context.language === "en" ? "tou_en.pdf" : "tou_cn.pdf"}#toolbar=0`}
										title="termsOfUse"
										className={styles.termsOfUse}
									></embed>
								) : (
									<div
										className={styles.modal}
										dangerouslySetInnerHTML={{ __html: infoModalType && t(`header.help.infoModal.${infoModalType}.content`) }}
									></div>
								)}
								<div className={styles.button}>
									<Button
										onClick={() => setInfoModalOpened(false)}
										color="success"
									>
										{infoModalType && t(`header.help.infoModal.${infoModalType}.cancel`)}
									</Button>
								</div>
							</div>
						</div>
					</Dialog>
				</div>
				<div className="dropdown dropdown-end">
					<Tooltip
						title={t("header.myAccount")}
						placement="bottom"
						slotProps={{
							popper: {
								modifiers: [
									{
										name: 'offset',
										options: {
											offset: [0, -12],
										},
									},
								],
							},
						}}
					>
						<IconButton
							id="avatar-button"
							aria-controls={avataOpen ? "avatar-menu" : undefined}
							aria-haspopup="true"
							aria-expanded={avataOpen ? "true" : undefined}
							onClick={handleAvatarClick}
							role="button"
							className="avatar placeholder ml-[10px]"
						>
							<div className="bg-[#d0d9d8] text-neutral-content rounded-full w-8 h-8">
								<span className="text-[#333] text-[15px] font-medium">{miniName}</span>
							</div>
						</IconButton>
					</Tooltip>
					<Menu
						id="avatar-menu"
						anchorEl={avatarEl}
						open={avataOpen}
						onClose={handleAvatarClose}
						MenuListProps={{
							"aria-labelledby": "avatar-button",
						}}
						PaperProps={{
							sx: {
								"& .MuiList-root": {
									py: 0,
								},
							},
						}}
					>
						<div className="card compact dropdown-content z-[1] p-0 shadow bg-base-100 mt-[10px] w-[310px] rounded-[4px] overflow-hidden">
							<div className="flex items-center text-[14px] text-[#0009] px-4 h-12 rounded-none">{t("header.account")}</div>
							<div className="border-t-[1px] border-solid border-[#0000001f]"></div>
							<div className="p-6 text-center">
								<div className="text-[22px] mb-[5px]">{userName}</div>
								<div className="text-[14px] text-[#0009] mb-6">{loginAccount}</div>
								{/* <div className="flex items-center bg-[#627d77] h-[18px] w-fit rounded-[9px] py-px px-2 m-auto">
                <span className="text-white text-[12px]">Internal</span>
              </div> */}
							</div>
							<div className="border-t-[1px] border-solid border-[#0000001f]"></div>
							{context.userRoles && (
								<div className="flex items-center justify-between text-[14px] text-[#0009] px-4 h-12 rounded-none">
									<div className="flex items-center">
										<span className="material-icons-outlined mr-4 w-6">star</span>
										<span>{t("header.membership")}</span>
									</div>
									<div
										className="flex items-center justify-center rounded-[15px] text-white text-[12px] py-[3px] px-[8px] h-[25px] min-w-[60px]"
										style={{
											background: systemBg(),
										}}
									>
										{systemSvg({ className: "mr-1 w-4 h-4" })}
										<span>{systemText()}</span>
									</div>
								</div>
							)}
							<div className="border-t-[1px] border-solid border-[#0000001f]"></div>
							<div
								className={`${styles.dropdownContent} flex items-center text-[14px] text-[#0009] px-4 h-12`}
								onClick={logoutAccount}
								role="button"
								tabIndex={0}
							>
								<LogoutSvg className="mr-4 fill-[#0000008a] w-6"></LogoutSvg>
								<span className="font-normal">{t("header.signOut")}</span>
							</div>
						</div>
					</Menu>
				</div>

				<div className="flex">
					<div
						className={getLangClass(langEn)}
						onClick={() => changeLanguage(langEn)}
						role="button"
						tabIndex={0}
					>
						{t("header.lang.en")}
					</div>
					<div
						className={getLangClass(langCn)}
						onClick={() => changeLanguage(langCn)}
						role="button"
						tabIndex={0}
					>
						{t("header.lang.cn")}
					</div>
				</div>
			</div>

			{termsOfUseModalOpened && (
				<Dialog
					open={termsOfUseModalOpened}
					onClose={() => setTermsOfUseModalOpened(false)}
					sx={{
						"& .MuiDialog-paper": {
							maxWidth: "80vw",
							maxHeight: "85vh",
							borderRadius: "4px",
							margin: "0"
						}
					}}
				>
					<div className={styles.DialogContainer}>
						<div className={styles.DialogHeader}>{t(`termsOfUse.title`)}</div>
						<div className={styles.text}>
							<embed
								src={`${process.env.PUBLIC_URL}/tou/${context.language === "en" ? "tou_en.pdf" : "tou_cn.pdf"}#toolbar=0`}
								title="termsOfUse"
								className={styles.termsOfUse}
							></embed>
							<FormGroup style={{ width: "max-content" }}>
								<FormControlLabel
									control={
										<Checkbox
											color="success"
											checked={termsOfUseChecked}
											onChange={(e) => setTermsOfUseChecked(e.target.checked)}
										/>
									}
									label={t(`termsOfUse.agreement`)}
								/>
							</FormGroup>
							<div className={styles.modalBtnBody}>
								<div style={{ marginRight: "10px" }}>
									<Button
										color="success"
										onClick={logoutAccount}
									>
										{t(`buttons.cancel`)}
									</Button>
								</div>
								<div>
									<Button
										variant="contained"
										color="success"
										onClick={() => handleTermsOfUseConfirm()}
										disabled={!termsOfUseChecked}
									>
										{t(`buttons.confirm`)}
									</Button>
								</div>
							</div>
						</div>
					</div>
				</Dialog>
			)}
		</div>
	);
}

export default Header;
